import { GET_ARTICLE, GET_SEARCH_QUERY_JOB_PROGRESS, REMAINING_WEBSITES, SEARCH_KEYWORD } from '../constants/url';
import axios from '../utils/axios';
import { checkAuth, getCachedUserProfile } from './auth';

// Function to get search queries
export const getSearchQueries = async () => {
  try {
    const { token } = checkAuth();

    const response = await axios.get('/get-search-query', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching search queries:', error);
    throw error;
  }
};

export const getKeywordsByQueryId = async (queryId) => {
  try {
    const { token } = checkAuth();
    // Axios call with Authorization header
    const response = await axios.get(`/get-search-query/${queryId}`, {
      headers: {
        Authorization: `Bearer ${token}`, // Attach token if it exists
      },
    });
    return response.data.original?.keywords; // Return the API response
  } catch (error) {
    console.error('Error fetching keywords:', error);
    throw error;
  }
};

export const getWebsitesByMainKeyword = async (mainKeywordId, locationId, page, perPage, filters = {}) => {
  try {
    const { token } = checkAuth();

    const filterParams = {
      main_keyword_id: mainKeywordId,
      location_id: locationId,
      page,
      limit: perPage,
      has_valid_email: filters?.has_valid_email ? 1 : 0,
      include_page_ascore: filters?.include_page_ascore ? 1 : 0,
      domain_rating: filters?.domain_rating || '',
      intersection_count: filters?.intersection_count || '',
      entry_method: filters?.entry_method || '',
      ranking: filters?.ranking || '',
      has_social_links: filters?.has_social_links ? 1 : 0,
      has_phone: filters?.has_phone ? 1 : 0,
      has_address: filters?.has_address ? 1 : 0,
      is_my_website_backlink: filters?.is_my_website_backlink ? 1 : 0,
      query_type: filters?.query_type || '',
      exclude_domains: filters?.exclude_domains || '',
      exclude_domain_type: filters?.exclude_domain_type,
      has_inquiry_form: filters?.has_inquiry_form ? 1 : 0,
      search: filters?.search || '',
      sort_order: filters?.sort_order || '',
      sorting_column: filters?.sorting_column || '',
    };

    const response = await axios.post('/websites', filterParams, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      paramsSerializer: (params) => {
        return Object.entries(params)
          .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
          .join('&');
      },
    });

    return {
      websites: response.data.data,
      meta: response.data.meta,
    };
  } catch (error) {
    console.error('Error fetching websites by main keyword:', error);
    throw error;
  }
};

export const getAllWebsitesByMainKeyword = async (mainKeywordId, locationId, filters = {}) => {
  try {
    const { token } = checkAuth();
    const filterParams = {
      main_keyword_id: mainKeywordId,
      location_id: locationId,
      has_valid_email: filters?.has_valid_email ? 1 : 0,
      include_page_ascore: filters?.include_page_ascore ? 1 : 0,
      domain_rating: filters?.domain_rating || '',
      intersection_count: filters?.intersection_count || '',
      entry_method: filters?.entry_method || '',
      ranking: filters?.ranking || '',
      has_social_links: filters?.has_social_links ? 1 : 0,
      has_phone: filters?.has_phone ? 1 : 0,
      has_address: filters?.has_address ? 1 : 0,
      is_my_website_backlink: filters?.is_my_website_backlink ? 1 : 0,
      query_type: filters?.query_type || '',
      exclude_domains: filters?.exclude_domains || '',
      exclude_domain_type: filters?.exclude_domain_type,
      has_inquiry_form: filters?.has_inquiry_form ? 1 : 0,
      search: filters?.search || '',
      sort_order: filters?.sort_order || '',
      sorting_column: filters?.sorting_column || '',
    };
    const response = await axios.post('/websites/all', filterParams, {
      headers: {
        Authorization: `Bearer ${token}`,
      },

      paramsSerializer: (params) => {
        return Object.entries(params)
          .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
          .join('&');
      },
    });

    return {
      websites: response.data.data,
      meta: response.data.meta,
    };
  } catch (error) {
    console.error('Error fetching all websites by main keyword:', error);
    throw error;
  }
};

export const searchAPI = async (params) => {
  try {
    const { token } = checkAuth();

    const queryParams = new URLSearchParams(params).toString();

    // Axios call to get progress for all initial queries
    const response = await axios.get(SEARCH_KEYWORD(queryParams), {
      headers: {
        Authorization: `Bearer ${token}`, // Attach token if it exists
      },
    });
    return response.data; // Return the API response
  } catch (error) {
    console.error('Error performing search:', error);
    throw error;
  }
};

//get sites for seo blog
export const searchUrlsForBlog = async (payload) => {
  try {
    const { token } = checkAuth();
    const response = await axios.get(`/get-sites-for-seo-blog`, {
      headers: {
        Authorization: `Bearer ${token}`, // Attach token if it exists
      },
      params: {
        query: payload.query, // Include query as a parameter
      },
    });
    return response.data; // Return the API response
  } catch (error) {
    console.error('Error performing search:', error);
    throw error;
  }
};

// Function to get backlinks and page_ascore by website IDs
export const getBacklinksData = async (websiteIds) => {
  try {
    const { token } = checkAuth();

    // Axios POST call with Authorization header
    const response = await axios.post(
      '/get-backlinks-by-website',
      { website_ids: websiteIds },
      {
        headers: {
          Authorization: `Bearer ${token}`, // Attach token if it exists
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching backlinks data:', error);
    throw error;
  }
};

export const getJobProgress = async (params) => {
  try {
    const { token } = checkAuth();

    const queryParams = new URLSearchParams(params).toString();

    // Axios call to get progress for all initial queries
    const response = await axios.get(GET_SEARCH_QUERY_JOB_PROGRESS(queryParams), {
      headers: {
        Authorization: `Bearer ${token}`, // Attach token if it exists
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching job progress:', error);
    throw error;
  }
};

export const saveAttackList = async (selectedRows) => {
  try {
    const { token } = checkAuth(); // Retrieve the auth token

    // Axios call to the backend to save the attack list
    const response = await axios.post(
      '/attack-list/store',
      { websites: selectedRows }, // Payload includes the selected rows
      {
        headers: {
          Authorization: `Bearer ${token}`, // Add Authorization header
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error saving attack list:', error);
    throw error;
  }
};

export const getAttackListByUser = async (sortOrder = '', sortingColumn = '') => {
  try {
    const { token } = checkAuth();
    const profile = await getCachedUserProfile();
    const response = await axios.get('/attack-list/get', {
      params: {
        user_id: profile?.id,
        sort_order: sortOrder,
        sorting_column: sortingColumn,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching attack list:', error);
    throw error;
  }
};

export const sendReportEmail = async (recipients, subject, body, bcc, attackListId) => {
  try {
    const { token } = checkAuth();
    const profile = await getCachedUserProfile();

    const response = await axios.post(
      '/send-test-email',
      {
        recipients,
        subject,
        body,
        bcc,
        user_id: profile?.id,
        from_email: profile?.email,
        from_name: profile?.name,
        attack_list_id: attackListId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

export const deleteAttackListItem = async (id) => {
  try {
    const { token } = checkAuth(); // Retrieve the auth token
    const profile = await getCachedUserProfile(); // Get the authenticated user's info

    // Axios call to the backend to delete the attack list item
    const response = await axios.delete(`/attack-list/delete/${id}`, {
      params: { user_id: profile?.id }, // Pass user_id as a query parameter
      headers: {
        Authorization: `Bearer ${token}`, // Add Authorization header
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting attack list item:', error);
    throw error;
  }
};

export const deleteDuplicateAttackListItems = async (ids) => {
  try {
    const { token } = checkAuth();
    const deleteIds = Array.isArray(ids) ? ids : [ids];

    const response = await axios.delete('/attack-list/delete-duplicate', {
      data: { ids: deleteIds },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting duplicate attack list items:', error);
    throw error;
  }
};

export const updateAttackListItem = async (id, data) => {
  try {
    const { token } = checkAuth(); // Retrieve the auth token
    const profile = await getCachedUserProfile(); // Get the authenticated user's info

    // Prepare the payload
    const payload = {
      email: data.email_address, // Use the email_address array directly
      phone: data.phone,
      status: data.status,
      user_id: profile?.id, // Include user_id in the payload
    };

    // Axios call to the backend to update the attack list item
    const response = await axios.put(`/attack-list/update/${id}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`, // Add Authorization header
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating attack list item:', error);
    throw error;
  }
};

export const getEmailNotification = async () => {
  try {
    // Temporarily disabled — do not call GET /get-email-notification
    return [];
    // const { token } = checkAuth(); // Retrieve the auth token
    // const profile = await getCachedUserProfile();
    // const reponse = await axios.get('/get-email-notification', {
    //   params: {
    //     user_id: profile?.id,
    //   },
    //   headers: {
    //     Authorization: `Bearer ${token}`,
    //   },
    // });
    // return reponse.data;
  } catch (error) {
    console.error('Error fetching email notification:', error);
    throw error;
  }
};

export const marksAsSeen = async (notificationID) => {
  try {
    const { token } = checkAuth();
    const profile = await getCachedUserProfile();

    if (!notificationID || !profile?.id) {
      console.error('Invalid notification ID or user ID');
      return;
    }

    const response = await axios.post(
      `/mark-as-seen/${notificationID}`, // Pass notificationID in URL, not body
      {
        user_id: profile.id, // Keep user_id in the body
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error marking as seen:', error);
    throw error;
  }
};

export const fetchFixedPhrases = async () => {
  try {
    const { token } = checkAuth();
    const profile = await getCachedUserProfile();
    const response = await axios.get('/fixed-phrases', {
      headers: { Authorization: `Bearer ${token}` },
      params: { user_id: profile?.id },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching fixed phrases:', error);
    throw error;
  }
};

export const saveFixedPhrase = async (fixedPhraseName, subject, emailBody) => {
  try {
    const { token } = checkAuth();
    const profile = await getCachedUserProfile();
    const response = await axios.post(
      '/fixed-phrases',
      {
        fixed_phrase_name: fixedPhraseName,
        subject,
        email_body: emailBody,
        created_by: profile?.id,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error('Error saving fixed phrase:', error);
    throw error;
  }
};

export const updateFixedPhrase = async (id, fixedPhraseName, subject, emailBody) => {
  try {
    const { token } = checkAuth();
    const response = await axios.put(
      `/fixed-phrases/${id}`,
      { fixed_phrase_name: fixedPhraseName, subject, email_body: emailBody },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating fixed phrase:', error);
    throw error;
  }
};

export const deleteFixedPhrase = async (id) => {
  try {
    const { token } = checkAuth();
    await axios.delete(`/fixed-phrases/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error) {
    console.error('Error deleting fixed phrase:', error);
    throw error;
  }
};

export const getFilterData = async (days, status, hasValidEmail, sortOrder = '', sortingColumn = '') => {
  try {
    const { token } = checkAuth();
    const profile = await getCachedUserProfile();
    const response = await axios.get('/attack-list/get-filter-data', {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        days,
        user_id: profile?.id,
        status,
        hasValidEmail,
        sort_order: sortOrder,
        sorting_column: sortingColumn,
      },
    });
    return response.data.filter_data;
  } catch (error) {
    console.error('Error fetching filter data:', error);
    throw error;
  }
};

// export const getJobProgress = async () => {
//    try {
//       const { token } = checkAuth();

//       // Axios call to the API without any parameters, as requested
//       const response = await axios.get('/get-job-progress', {
//          headers: {
//             Authorization: `Bearer ${token}`, // Attach token if it exists
//          },
//       });

//       // Return the job progress status ('completed' or 'in progress')
//       return response.data;
//    } catch (error) {
//       console.error('Error fetching job progress:', error);
//       throw error;
//    }
// };
export const getArticles = async (params) => {
  try {
    const { token } = checkAuth();
    const queryParams = new URLSearchParams(params).toString();
    const response = await axios.get(GET_ARTICLE(queryParams), {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error performing article search:', error);
    throw error;
  }
};

//get prompts by array of article ids
export const getPromptsByArticleIds = async (ids) => {
  try {
    const { token } = checkAuth();
    const response = await axios.get(`/get-prompts-by-article-ids`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        article_ids: ids, // Pass the array of article IDs as a parameter
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error performing prompts search:', error);
    throw error;
  }
};

export const getArticleByPromptId = async (id) => {
  try {
    const { token } = checkAuth();
    const response = await axios.get(`/get-article-by-prompt/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error performing article fetching:', error);
    throw error;
  }
};

export const generatePrompt = async (data) => {
  try {
    const { token } = checkAuth();
    const payload = {
      keyword: data.keyword,
      main_keyword_id: data.main_keyword_id || null,
      language: data.language,
      articleLimit: data.articleLimit,
      articleMode: data.articleMode,
      first_hand_info_mode: data.first_hand_info_mode,
      location: data.location,
      location_id: data.location_id,
      display_location: data.display_location,
      country_code: data.country_code,
      prefecture_id: data.prefecture_id,
      city_id: data.city_id,
      station_id: data.station_id,
      keyword_id: data.keyword_id || null,
      user_prompt: data?.user_prompt ?? '',
      make_ai_overview: data.make_ai_overview,
      is_question: data.is_question,
      is_meo: data.is_meo,
    };
    const response = await axios.post(`/generate-initial-prompt-and-article`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error performing prompt generation:', error);
    throw error;
  }
};

export const getLimitMode = async () => {
  try {
    const { token } = checkAuth();
    const response = await axios.get(`/master-prompts/prompt-mode`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching prompt limit:', error);
    throw error;
  }
};

export const getLanguageModeList = async () => {
  try {
    const { token } = checkAuth();
    const response = await axios.get(`/get-article-language-mode`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching prompt limit:', error);
    throw error;
  }
};

export const getAllPromptModes = async () => {
  try {
    const { token } = checkAuth();
    const response = await axios.get(`/master-prompts/prompt-types`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching prompt mode:', error);
    throw error;
  }
};

export const getPromptData = async (id) => {
  try {
    const { token } = checkAuth();
    const response = await axios.get(`/articles/${id}/prompt`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error performing prompt fetching:', error);
    throw error;
  }
};

export const deleteArticle = async (id) => {
  try {
    const { token } = checkAuth();
    const response = await axios.delete(`/delete-article/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error performing article deletion:', error);
    throw error;
  }
};

export const updatePromptData = async (id, data) => {
  try {
    const { token } = checkAuth();

    const payload = {
      related_searches: data.related_searches,
      suggested_keywords: data.suggestions,
      people_also_ask: data.related_questions,
      co_occurrence: data?.co_occurrence,
      co_occurrence_for_headings: data?.co_occurrence_for_headings,
      co_occurrence_for_titles: data?.co_occurrence_for_titles,
      competitor_headings: data?.competitor_headings,
      status: 3, // Article generation not started,
    };

    const response = await axios.put(`/update-prompt/${id}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error performing prompt updation:', error);
    throw error;
  }
};

export const updateArticlePostingData = async (prompt_id, article_id) => {
  try {
    if (!prompt_id || !article_id) {
      throw new Error('Missing prompt_id or article_id');
    }
    const { token } = checkAuth();
    if (!token) {
      throw new Error('Authentication token is missing');
    }

    const ARTICLE_POSTING_STATUS = 9;
    const payload = {
      prompt_id,
      article_id,
      status: ARTICLE_POSTING_STATUS,
    };

    const response = await axios.put('/articles-posting', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating article posting for prompt_id: ${prompt_id}, article_id: ${article_id}`, error);
    throw error;
  }
};

export const updateArticle = async (id, data) => {
  try {
    const { token } = checkAuth();

    const payload = {
      content: data.content,
      // character_count: data.character_count,
      article_segments: data.article_segments,
    };
    if (data.character_count > 0) {
      payload.character_count = data.character_count;
    }
    const response = await axios.put(`/update-article/${id}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error performing article updation:', error);
    throw error;
  }
};

export const generateArticle = async (id) => {
  try {
    const { token } = checkAuth();
    const payload = {
      prompt_id: id,
    };

    const response = await axios.post(`/generate-article`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error performing article generation', error);
    throw error;
  }
};

export const getScreenWiseBacklinkData = async (
  screen_id,
  location_id,
  main_keyword_id,
  page,
  perPage,
  filters = {}
) => {
  try {
    const { token } = checkAuth();

    const data = {
      screen_id: screen_id,
      location_id: location_id,
      main_keyword_id: main_keyword_id,
      page: page,
      limit: perPage,
      has_valid_email: filters?.has_valid_email ? 1 : 0,
      include_page_ascore: filters?.include_page_ascore ? 1 : 0,
      domain_rating: filters?.domain_rating || '',
      entry_method: filters?.entry_method || '',
      ranking: filters?.ranking || '',
      has_social_links: filters?.has_social_links ? 1 : 0,
      has_phone: filters?.has_phone ? 1 : 0,
      has_address: filters?.has_address ? 1 : 0,
      is_my_website_backlink: filters?.is_my_website_backlink ? 1 : 0,
      query_type: filters?.query_type || '',
      exclude_domains: filters?.exclude_domains || '',
      has_inquiry_form: filters?.has_inquiry_form ? 1 : 0,
      sentiments: filters?.sentiments || '',
      email_types: filters?.email_types || '',
      publication_statuses: filters?.publication_statuses || '',
      check_send_result: filters?.check_send_result || '',
      search: filters?.search || '',
      sort_order: filters?.sort_order || '',
      sorting_column: filters?.sorting_column || '',
      form_attack_auto_started_time: filters?.form_attack_auto_started_time || '',
      form_attack_auto_end_time: filters?.form_attack_auto_end_time || '',
      form_attack_manual_started_time: filters?.form_attack_manual_started_time || '',
      form_attack_manual_end_time: filters?.form_attack_manual_end_time || '',
      error_message_types: filters?.error_message_types || '',
      form_status_types: filters?.form_status_types || '',
      form_ng_word_status_types: filters?.form_ng_word_status_types || '',
      filter_by_duplicate: filters?.filter_by_duplicate ? 1 : 0,
    };

    const response = await axios.post('/backlink/screen-wise-data', data, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error performing article generation', error);
    throw error;
  }
};

export const updateScreenWiseBacklinkData = async (payload) => {
  const { token } = checkAuth();

  const response = await axios.put('/backlink/screen-wise-data-update', payload, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  return response.data;
};

export const getTopPageNotificationCountForBacklink = async ({ location_id, main_keyword_id }) => {
  const { token } = checkAuth();

  const response = await axios.post(
    '/backlink/home-screen-count',
    {
      location_id: location_id,
      main_keyword_id: main_keyword_id,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
};

export const bulkExcludeWebsites = async (data) => {
  const { token } = checkAuth();
  const response = await axios.post('/websites/bulk-exclude', data, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  return response.data;
};

export const handleAddMoreWebsite = async (data) => {
  try {
    const { token } = checkAuth();
    const response = await axios.post('/add-more-website', data, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    console.error('Error adding more websites:', error);
    throw error;
  }
};

export const getBacklinkFormAttackStatusCounts = async (params) => {
  try {
    const { token } = checkAuth();
    const queryParams = new URLSearchParams(params).toString();

    const response = await axios.get(`/get-form-attack-status-count?${queryParams}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching form attack status counts:', error);
    throw error;
  }
};

export const getRemainingWebsiteCount = async (data) => {
  try {
    const { token } = checkAuth();
    const response = await axios.post(REMAINING_WEBSITES, data, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching remaining website count:', error);
    throw error;
  }
};
